import { useEffect, useRef, useState } from 'react';
import { getJiro, sendMessage } from './JiroConnect';
import { IconButton, InputAdornment, OutlinedInput } from '@mui/material';
import { Send } from '@mui/icons-material';
import './AskJiro.css';
import { Container } from 'reactstrap';

type Message = {
    from: string;
    message: string;
};

const AskJiro = () => {
    const socketRef = useRef<WebSocket | null>(null);
    const [socket, setSocket] = useState<WebSocket>();
    const [connection, setConnection] = useState<any>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [userMsg, setUserMsg] = useState<string>('');
    const [typing, setTyping] = useState<boolean>(false);
    const [connMsg, setConnMsg] = useState<string | null>(null);
    const endMessageRef = useRef<HTMLDivElement>(null);

    const connect = async () => {
        setConnMsg('Connecting...');
        const connection = await getJiro((activity) => {
            if (activity.type === 'typing') {
                setTyping(true);
                setConnMsg(null);
            }
            if (activity.text) {
                setMessages(prev => [
                    { from: activity.from.role, message: activity.text },
                    ...prev
                ]);
                setTyping(false);
                setConnMsg(null);
            }
        });

        if (connection) {
            socketRef.current = connection.socket ?? null;

            setSocket(connection?.socket);
            setConnection(connection);
            setConnMsg(null);
        }
    };

    useEffect(() => {
        console.log('ASK JIRO MOUNT');
        connect();

        return () => {
            console.log('ASK JIRO UNMOUNT');
            socketRef.current?.close();
        };

    }, []);

    useEffect(() => {
        endMessageRef.current?.scrollIntoView({
            behavior: 'smooth'
        });
    }, [messages]);

    const handleSend = async () => {
        const msg = userMsg;

        setMessages(prev => [
            { from: 'user', message: msg },
            ...prev,
        ]);

        setUserMsg('');

        try {
            if (!connection || !socket || socket.readyState !== WebSocket.OPEN) {
                throw new Error("Connection lost");
            }
            await sendMessage(
                connection.conversationId,
                connection.token,
                msg
            )
        } catch (e) {
            setConnMsg('Send Failed – Reconnecting...');

            const newConn = await getJiro((activity) => {
                if (activity.type === 'typing') {
                    setTyping(true);
                }
                if (activity.text) {
                    if (activity.text.includes("Hello, I'm Ask Jiro")) return;
                    setMessages(prev => [
                        { from: activity.from.role, message: activity.text },
                        ...prev,
                    ]);
                    setTyping(false);
                }
            });

            if (newConn) {
                setConnMsg('Reonnect Success! Resending your message.')
                setSocket(newConn?.socket);
                setConnection(newConn);

                await sendMessage(
                    newConn.conversationId,
                    newConn.token,
                    msg
                );
            };
        } finally {
            setConnMsg(null);
        }
    };
    
    return (
        <Container>
            <div className='chat-container'>
                {[...messages].reverse().map((item, index) => (
                    <div
                        key={index} 
                        className={item.from === 'user' ? 'userMsg' : 'botMsg'}
                    >
                        {item.message}
                    </div>
                ))}
                <div ref={endMessageRef} />
            </div>
            <div
                style={{ minHeight: 30 }} 
                className='d-flex'
            >
                {connMsg && 
                    <div style={{ marginInlineStart: 10, }}>
                        <span>{connMsg}</span>
                    </div>
                }
                {typing &&
                    <div style={{ marginInlineStart: 10, }}>
                        <span>Jiro is Typing...</span>
                    </div>
                }
            </div>
            <div
                className='d-flex align-items-center gap-1 justify-content-center'
            >
                <OutlinedInput
                    multiline
                    placeholder='Send a message'
                    onChange={(e) => setUserMsg(e.target.value)}
                    value={userMsg}
                    fullWidth
                    sx={{
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#f1b434',
                        },
                    }}  
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSend();
                        }
                    }}
                    endAdornment={
                        <InputAdornment position='end'>
                            <IconButton
                                onClick={handleSend}
                                edge='end'
                            >
                                <Send sx={{ color: '#f1b434' }}/>            
                            </IconButton>
                        </InputAdornment>
                    }
                />
            </div>
        </Container>
    )
};

export default AskJiro;